/**
 * Rarity tiers as the design system names them. Index 0–4 are the platform's `getItemRarity`
 * values; 5 is the chase tier the platform reserves (spec, section 4).
 */
export const RARITY_TIERS = ["common", "uncommon", "rare", "epic", "legendary", "chase"] as const;
export type RarityTier = (typeof RARITY_TIERS)[number];

/** A platform rarity index (0–5) as a tier; anything else reads as common. */
export function rarityTier(index: number): RarityTier {
  return RARITY_TIERS[index] ?? "common";
}

/** Each tier's fill, for a dot or a bar segment. Complete literals, so Tailwind sees them. */
export const RARITY_FILL: Record<RarityTier, string> = {
  common: "bg-rarity-common",
  uncommon: "bg-rarity-uncommon",
  rare: "bg-rarity-rare",
  epic: "bg-rarity-epic",
  legendary: "bg-rarity-legendary",
  chase: "bg-rarity-chase",
};

/** The default English name of each tier, for screen readers and legends; copy can replace it. */
export const RARITY_LABELS: Record<RarityTier, string> = {
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
  chase: "Chase",
};

/** A tier's color as text: a pull's headline, its odds. */
export const RARITY_TEXT: Record<RarityTier, string> = {
  common: "text-rarity-common",
  uncommon: "text-rarity-uncommon",
  rare: "text-rarity-rare",
  epic: "text-rarity-epic",
  legendary: "text-rarity-legendary",
  chase: "text-rarity-chase",
};

/**
 * Each tier's color for words that must read (`rarity-text`: the tier itself where it reads on the
 * theme, else nudged toward the text just far enough). Complete names, so Tailwind emits them.
 */
export const RARITY_READABLE_TEXT: Record<RarityTier, string> = {
  common: "text-rarity-text-common",
  uncommon: "text-rarity-text-uncommon",
  rare: "text-rarity-text-rare",
  epic: "text-rarity-text-epic",
  legendary: "text-rarity-text-legendary",
  chase: "text-rarity-text-chase",
};

/** A tier's color as an edge. */
export const RARITY_BORDER: Record<RarityTier, string> = {
  common: "border-rarity-common",
  uncommon: "border-rarity-uncommon",
  rare: "border-rarity-rare",
  epic: "border-rarity-epic",
  legendary: "border-rarity-legendary",
  chase: "border-rarity-chase",
};
